import { AnyObject } from '@/models/GenericInterfaces'
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import FileSaver from 'file-saver'
import { useDefaultStore } from '@/store'
import { mergeObjects } from '@/utils/tools'
import AuthService from './AuthService'

type StreamOptions = {
  method?: 'GET' | 'POST'
  version?: string
  body?: AnyObject
  query?: URLSearchParams
  signal?: AbortSignal
  isPublicRoute?: boolean
  headers?: Record<string, string>
}

class ApiService {
  private get store() {
    return useDefaultStore()
  }

  getApiUrl(protocol: 'http' | 'ws') {
    let usedProtocol = protocol
    if (!this.store.apiHost.startsWith('localhost')) {
      usedProtocol += 's'
    }

    return `${usedProtocol}://${this.store.apiHost}/api`
  }

  /**
   * @param path endpoint path not including /api/{version}
   * @param method http method
   * @param version usually 'v1' or 'v2'. 'v1' by default
   * @param body payload for post/put/patch requests
   * @param config
   * @param isPublicRoute whether or not to include authorization token
   * @returns
   */
  private async request<T>(
    path: string,
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    version: string,
    body?: AnyObject,
    config?: AxiosRequestConfig,
    isPublicRoute?: boolean,
  ) {
    let response: AxiosResponse<T>

    if (!isPublicRoute) {
      config = mergeObjects(undefined, config || {}, {
        headers: { Authorization: `Bearer ${await AuthService.getUserToken()}` },
      })
    }

    if (['post', 'put', 'patch'].includes(method)) {
      response = await axios[method](`${this.getApiUrl('http')}/${version}/${path}`, body, config)
    } else {
      response = await axios[method](`${this.getApiUrl('http')}/${version}/${path}`, config)
    }

    return response
  }

  async get<T = unknown>(path: string, config?: AxiosRequestConfig, version = 'v1', isPublicRoute?: boolean) {
    const response = await this.request<T>(path, 'get', version, undefined, config, isPublicRoute)
    return response.data
  }

  async post<T = unknown>(path: string, body?: AnyObject, config?: AxiosRequestConfig, version = 'v1') {
    const response = await this.request<T>(path, 'post', version, body, config)
    return response.data
  }

  /**
   * Semantic difference to PATCH - expected to replace the object with the supplied data rather than complementing it
   */
  async put<T = unknown>(path: string, body?: AnyObject, config?: AxiosRequestConfig, version = 'v1') {
    const response = await this.request<T>(path, 'put', version, body, config)
    return response.data
  }

  /**
   * Semantic difference to PUT - expected to patch the object with the supplied data rather than replacing it
   */
  async patch<T = unknown>(path: string, body?: AnyObject, config?: AxiosRequestConfig, version = 'v1') {
    const response = await this.request<T>(path, 'patch', version, body, config)
    return response.data
  }

  async delete<T = unknown>(path: string, config?: AxiosRequestConfig, version = 'v1') {
    const response = await this.request<T>(path, 'delete', version, undefined, config)
    return response.data
  }

  /**
   *
   * @param fallbackFileName normally uses fileName from the content-disposition header
   * @returns
   */
  async download(path: string, fallbackFileName?: string, version = 'v1') {
    const response = await this.request<any>(path, 'get', version, undefined, { responseType: 'blob' })

    const blob = new Blob([response.data], { type: response.headers['content-type']?.toString() })
    const contentDispositionFileName = response.headers['content-disposition']?.match(/filename\*?="?(?:UTF-8'')?([^"]+)"?/)?.[1]

    if (blob?.size) {
      FileSaver.saveAs(blob, contentDispositionFileName ? decodeURIComponent(contentDispositionFileName) : fallbackFileName)
    }
    return response
  }

  /**
   *
   * @param path
   * @param params
   * @param payload
   * @param version
   * @returns
   */
  async ws(path: string, params?: URLSearchParams, payload?: AnyObject, version = 'v1'): Promise<WebSocket> {
    const userToken = await AuthService.getUserToken()

    if (!userToken) {
      throw new Error('No user token')
    }

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(`${this.getApiUrl('ws')}/${version}/${path}?authorization=${encodeURIComponent(userToken)}&${params?.toString()}`)

      ws.onmessage = (msg) => {
        // handshake - safer than onopen with express-ws
        if (msg.data === 'ready') {
          ws.send(payload ? JSON.stringify(payload) : 'start')
          resolve(ws)
        }
      }

      ws.onclose = (e) => {
        console.warn(`Code ${e.code}. Connection closed before handshake.`, e.reason)
        reject()
      }

      ws.onerror = (e) => {
        console.warn('Connection errored before handshake')
        reject()
      }
    })
  }

  /**
   * Sends post data even if the tab closes
   */
  async sendBeacon(path: string, data: AnyObject, version = 'v1') {
    const authToken = await AuthService.getUserToken()
    if (authToken) {
      const params = new URLSearchParams({ authorization: authToken })
      const url = `${this.getApiUrl('http')}/${version}/${path}?${params.toString()}`
      navigator.sendBeacon(url, JSON.stringify(data))
    }
  }

  /**
   * Requests data from the backend API via stream
   * @returns an async iterator, iterating over stream values
   */
  async *stream<T = string>(path: string, options: StreamOptions = {}): AsyncGenerator<T, void, unknown> {
    const { method = 'GET', version = 'v1', body, query, signal, isPublicRoute, headers } = options

    const token = isPublicRoute ? undefined : await AuthService.getUserToken()
    if (!isPublicRoute && !token) {
      throw new Error('No user token')
    }

    const url = `${this.getApiUrl('http')}/${version}/${path}${query ? `?${query.toString()}` : ''}`

    const response = await fetch(url, {
      method,
      signal,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    })

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status} ${response.statusText}`)
    }
    if (!response.body) {
      throw new Error('Readable stream not available')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split('\n')
        buffer = parts.pop() ?? ''

        for (const raw of parts) {
          yield JSON.parse(raw) as T
        }
      }
    } finally {
      reader.releaseLock()
    }
  }

  public async reportError(err: Error) {
    this.sendBeacon('error-reporting', { msg: err.message, stack: err.stack })
  }
}
export default new ApiService()
